import { t } from "@/context/language";
import {
  CONSENT_STORAGE_KEY,
  clearPosthogStorage,
  readConsent,
  type ConsentValue,
} from "@/lib/analyticsConsent";
import { posthog } from "@/lib/posthogClient";
import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useState } from "react";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: Array<Record<string, unknown>>;
  }
}

function applyConsent(value: ConsentValue) {
  if (typeof window === "undefined") return;

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      ad_storage: value,
      ad_user_data: value,
      ad_personalization: value,
      analytics_storage: value,
    });
  }

  // PostHog: o "Aceitar" passa a gravar o id em cookie/localStorage; o
  // "Recusar" mantém tudo na memória e apaga o que tiver sobrado. No site
  // público, o before_send (src/main.tsx) já bloqueia o envio sem aceite.
  if (posthog.__loaded) {
    posthog.set_config({
      persistence: value === "granted" ? "localStorage+cookie" : "memory",
    });
  }
  if (value === "denied") clearPosthogStorage();

  window.dataLayer = window.dataLayer ?? [];
  window.dataLayer.push({
    event:
      value === "granted" ? "cookie_consent_granted" : "cookie_consent_denied",
  });
}

export function CookieConsent() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (readConsent() === null) setVisible(true);
  }, []);

  const decide = (value: ConsentValue) => {
    try {
      localStorage.setItem(CONSENT_STORAGE_KEY, value);
    } catch {
      // localStorage indisponível (ex.: modo anônimo restrito) — segue só com a sessão
    }
    applyConsent(value);
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          role="dialog"
          aria-live="polite"
          aria-label="Aviso de cookies"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
          className="fixed right-4 bottom-4 left-4 z-[60] max-w-md rounded-2xl border border-gray-200 bg-white p-5 shadow-xl sm:right-auto sm:left-6"
        >
          <p className="text-sm leading-relaxed text-gray-700">
            {t.cookies.text}
          </p>

          <div className="mt-4 flex gap-3">
            <button
              type="button"
              onClick={() => decide("granted")}
              className="bg-primary hover:bg-primary/90 inline-flex h-11 flex-1 cursor-pointer items-center justify-center rounded-md px-4 text-sm font-bold text-white transition-colors"
            >
              {t.cookies.accept}
            </button>
            <button
              type="button"
              onClick={() => decide("denied")}
              className="inline-flex h-11 flex-1 cursor-pointer items-center justify-center rounded-md border border-gray-300 px-4 text-sm font-bold text-black transition-colors hover:bg-gray-100"
            >
              {t.cookies.decline}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
